import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, MessageSquare } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { FAQ } from '../../constants/faq';

export function FAQSection() {
  const [active, setActive] = useState<number | null>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="faq" aria-label="Frequently asked questions about interior design" className={`py-20 sm:py-28 transition-colors duration-500 relative overflow-hidden ${isDark ? 'bg-[#0B0F1A]' : 'bg-slate-50'}`}>
      
      {/* Decorative Blur Backdrops */}
      <div className="absolute top-1/3 -left-48 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-48 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Asymmetrical Grid Layout */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          
          {/* Left Column: Premium Editorial Image & Badge */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                <span className="text-xs uppercase font-poppins tracking-wider font-semibold text-accent">COMMON QUERIES</span>
              </div>

              <h2 className={`text-2xl sm:text-4xl lg:text-[42px] font-poppins font-bold leading-tight tracking-tight ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
                Frequently Asked <span className="text-accent">Questions</span>
              </h2>

              <p className={`text-sm sm:text-base leading-relaxed ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                Everything you need to know about our premium design workflow, execution speeds, structural warranties, and raw material standards.
              </p>

              {/* High-End Editorial Image Block */}
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl border border-white/5 group">
                {/* Visual Related FAQ Graphic Overlay */}
                <div className="absolute top-3 left-3 right-3 sm:top-4 sm:left-4 sm:right-4 p-3 rounded-2xl bg-black/50 backdrop-blur-md border border-white/10 text-white select-none z-10 space-y-2">
                  <div className="text-[9px] uppercase font-poppins tracking-widest font-extrabold text-accent">Quick Reference FAQ</div>
                  <div className="space-y-1.5 text-[10px] font-semibold">
                    <div className="flex justify-between border-b border-white/5 pb-1">
                      <span className="text-white/80">Average 2BHK Cost</span>
                      <span className="text-accent font-bold">Starts from ₹3.5L</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-1">
                      <span className="text-white/80">Delivery Timeline</span>
                      <span className="text-accent font-bold">45-60 Days Guaranteed</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-1">
                      <span className="text-white/80">Peace of Mind</span>
                      <span className="text-accent font-bold">Comprehensive Coverage</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">3D Visualizations</span>
                      <span className="text-accent font-bold">Free / 100% Included</span>
                    </div>
                  </div>
                </div>

                <div className="absolute inset-0 bg-accent/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none blur-xl" />
                <img 
                  src="/images/projects/rajpushpa-provincia/Provincia.jpg" 
                  alt="Premium Luxury Interior Design"
                  className="w-full h-full object-cover brightness-[0.85] contrast-[1.05] transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0B0F1A]/85' : 'from-slate-900/80'} via-transparent to-transparent`} />
                
                {/* Floating details badge */}
                <div className="absolute bottom-3 left-3 right-3 sm:bottom-5 sm:left-5 sm:right-5 p-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 text-white flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <strong className="text-[10px] font-poppins font-bold block">Need Immediate Support?</strong>
                    <span className="text-[9px] text-white/70">Connect with our execution heads</span>
                  </div>
                  <a 
                    href="https://wa.me/919989697942" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded-xl bg-accent text-white text-[9px] font-poppins font-extrabold uppercase tracking-wider hover:bg-accent-hover transition-colors flex items-center gap-1 shrink-0"
                  >
                    <MessageSquare className="w-3 h-3" />
                    Chat Now
                  </a>
                </div>
              </div>

            </motion.div>
          </div>

          {/* Right Column: FAQ Accordion list */}
          <div className="lg:col-span-7 space-y-4">
            {FAQ.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(i * 0.05, 0.35), duration: 0.4 }}
                className={`rounded-2xl border shadow-xs overflow-hidden transition-all duration-300 ${
                  active === i 
                    ? isDark 
                      ? 'bg-[#1E293B] border-accent/40 shadow-md shadow-black/20' 
                      : 'bg-white border-accent/30 shadow-md shadow-slate-100/50' 
                    : isDark 
                      ? 'bg-[#0E1626] border-white/[0.04] hover:border-white/10' 
                      : 'bg-white border-slate-200/60 hover:border-slate-300'
                }`}
              >
                <button
                  onClick={() => setActive(active === i ? null : i)}
                  className={`w-full flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 text-left group transition-colors cursor-pointer ${
                    isDark ? 'hover:bg-white/[0.01]' : 'hover:bg-slate-50/50'
                  }`}
                >
                  <span className={`font-poppins font-bold text-[13px] sm:text-[15px] pr-3 sm:pr-4 transition-colors leading-snug ${
                    active === i 
                      ? 'text-accent' 
                      : isDark ? 'text-white group-hover:text-accent' : 'text-[#0F172A] group-hover:text-accent'
                  }`}>
                    {faq.q}
                  </span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                    active === i
                      ? 'bg-accent text-white rotate-180'
                      : isDark ? 'bg-white/5 text-gray-400' : 'bg-slate-100 text-slate-500'
                  }`}>
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </button>
                
                <AnimatePresence initial={false}>
                  {active === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ height: { duration: 0.35, ease: [0.04, 0.62, 0.23, 0.98] }, opacity: { duration: 0.25 } }}
                      className="overflow-hidden"
                    >
                      <div className={`px-4 sm:px-6 pb-4 sm:pb-5 text-sm leading-relaxed whitespace-pre-line border-t pt-3 sm:pt-4 font-medium ${
                        isDark ? 'text-gray-300 border-white/[0.04]' : 'text-slate-650 border-slate-100'
                      }`}>
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
